import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, Container, Row, Col, Alert, Form, Tab, Nav, Table, Badge, Spinner } from 'react-bootstrap';
import CustomNavbar from './CustomNavbar'; // Import the navbar component
import { FaUser, FaEnvelope, FaPhone, FaUserTag, FaCalendarAlt, FaHome, FaMapMarkerAlt, FaBell, FaPlus, FaStickyNote, FaTrash, FaEdit, FaCheckCircle, FaClock } from 'react-icons/fa'; // Import icons
import { apiBaseUrl } from '../config.js';

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  
  // Appointments state
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState('');
  
  // Follow-ups state
  const [followUps, setFollowUps] = useState([]);
  const [followUpsLoading, setFollowUpsLoading] = useState(false);
  const [followUpsError, setFollowUpsError] = useState('');
  const [followUpsStats, setFollowUpsStats] = useState({ total: 0, pending: 0, completed: 0, overdue: 0 });
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [followUpForm, setFollowUpForm] = useState({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    reminderDate: '',
    notes: ''
  });
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    // Fetch users when the component mounts
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('userToken'); // Assuming the token is stored in localStorage
        const response = await axios.get(`${apiBaseUrl}/api/admin/users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            limit: 1000 // Get all users
          }
        });

        if (!response.data.success || response.data.users.length === 0) {
          setError('No users found'); // Show error if no users are returned
        }

        const usersData = response.data.users.map(user => ({
          ...user,
          username: fixNameFormat(user.name),
          userLname: fixNameFormat(user.lname),
          user: user.userType, // Map userType to user field for compatibility
          premiumCredits: user.premiumCredits || 0
        }));

        // Sort users: contacted ones show last
        const sortedUsers = usersData.sort((a, b) => (a.contacted === b.contacted ? 0 : a.contacted ? 1 : -1));

        setUsers(sortedUsers); // Store users in state
        setFilteredUsers(sortedUsers); // Set initial filtered users
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Error fetching users. Please try again.');
      }
    };

    fetchUsers();
  }, []);

  // Fetch appointments when tab changes to appointments
  useEffect(() => {
    if (activeTab === 'appointments') {
      fetchAppointments();
    }
    if (activeTab === 'followups') {
      fetchFollowUps();
    }
  }, [activeTab]);

  const fetchAppointments = async () => {
    setAppointmentsLoading(true);
    setAppointmentsError('');
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.get(`${apiBaseUrl}/api/admin/martillero-appointments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.data.success) {
        setAppointments(response.data.appointments);
      } else {
        setAppointmentsError('Error al cargar las citas');
      }
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setAppointmentsError('Error al cargar las citas. Por favor intente de nuevo.');
    } finally {
      setAppointmentsLoading(false);
    }
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      const token = localStorage.getItem('userToken');
      await axios.put(`${apiBaseUrl}/api/admin/martillero-appointments/${appointmentId}`, 
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Update local state
      setAppointments(prev => prev.map(apt => 
        apt._id === appointmentId ? { ...apt, status: newStatus } : apt
      ));
    } catch (err) {
      console.error('Error updating appointment status:', err);
      setAppointmentsError('Error al actualizar el estado');
    }
  };

  // Follow-ups functions
  const fetchFollowUps = async () => {
    setFollowUpsLoading(true);
    setFollowUpsError('');
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.get(`${apiBaseUrl}/api/admin/follow-ups`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          limit: 100 // Get more for now
        }
      });
      
      if (response.data.success) {
        setFollowUps(response.data.followUps);
        setFollowUpsStats(response.data.stats);
      } else {
        setFollowUpsError('Error al cargar seguimientos');
      }
    } catch (err) {
      console.error('Error fetching follow-ups:', err);
      setFollowUpsError('Error al cargar seguimientos. Por favor intente de nuevo.');
    } finally {
      setFollowUpsLoading(false);
    }
  };

  const handleCreateFollowUp = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('userToken');
      await axios.post(`${apiBaseUrl}/api/admin/follow-ups`, 
        followUpForm,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Reset form and close modal
      setFollowUpForm({
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        reminderDate: '',
        notes: ''
      });
      setShowFollowUpModal(false);
      setEditingFollowUp(null);
      
      // Refresh list
      fetchFollowUps();
    } catch (err) {
      console.error('Error creating follow-up:', err);
      setFollowUpsError('Error al crear seguimiento');
    }
  };

  const handleUpdateFollowUp = async (e) => {
    e.preventDefault();
    if (!editingFollowUp) return;
    
    try {
      const token = localStorage.getItem('userToken');
      await axios.put(`${apiBaseUrl}/api/admin/follow-ups/${editingFollowUp._id}`, 
        followUpForm,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Reset form and close modal
      setFollowUpForm({
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        reminderDate: '',
        notes: ''
      });
      setShowFollowUpModal(false);
      setEditingFollowUp(null);
      
      // Refresh list
      fetchFollowUps();
    } catch (err) {
      console.error('Error updating follow-up:', err);
      setFollowUpsError('Error al actualizar seguimiento');
    }
  };

  const handleDeleteFollowUp = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este seguimiento?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('userToken');
      await axios.delete(`${apiBaseUrl}/api/admin/follow-ups/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Refresh list
      fetchFollowUps();
    } catch (err) {
      console.error('Error deleting follow-up:', err);
      setFollowUpsError('Error al eliminar seguimiento');
    }
  };

  const handleAddNote = async (followUpId) => {
    if (!newNote.trim()) return;
    
    try {
      const token = localStorage.getItem('userToken');
      await axios.post(`${apiBaseUrl}/api/admin/follow-ups/${followUpId}/notes`, 
        { note: newNote },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setNewNote('');
      
      // Refresh list
      fetchFollowUps();
    } catch (err) {
      console.error('Error adding note:', err);
      setFollowUpsError('Error al agregar nota');
    }
  };

  const handleMarkAsCompleted = async (id) => {
    try {
      const token = localStorage.getItem('userToken');
      await axios.put(`${apiBaseUrl}/api/admin/follow-ups/${id}`, 
        { status: 'completed' },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Update local state
      setFollowUps(prev => prev.map(fu => 
        fu._id === id ? { ...fu, status: 'completed' } : fu
      ));
    } catch (err) {
      console.error('Error marking as completed:', err);
      setFollowUpsError('Error al marcar como completado');
    }
  };

  const openEditModal = (followUp) => {
    setEditingFollowUp(followUp);
    setFollowUpForm({
      clientName: followUp.clientName,
      clientPhone: followUp.clientPhone,
      clientEmail: followUp.clientEmail || '',
      reminderDate: followUp.reminderDate ? new Date(followUp.reminderDate).toISOString().split('T')[0] : '',
      notes: ''
    });
    setShowFollowUpModal(true);
  };

  const openCreateModal = () => {
    setEditingFollowUp(null);
    setFollowUpForm({
      clientName: '',
      clientPhone: '',
      clientEmail: '',
      reminderDate: '',
      notes: ''
    });
    setShowFollowUpModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'warning', text: 'Pendiente' },
      contacted: { bg: 'info', text: 'Contactado' },
      completed: { bg: 'success', text: 'Completado' },
      cancelled: { bg: 'danger', text: 'Cancelado' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

  const fixNameFormat = (name) => {
    // Add a space if no space exists between lowercase and uppercase
    return name.replace(/([a-z])([A-Z])/g, '$1 $2');
  };

  const handleSearch = (event) => {
    const searchValue = event.target.value.toLowerCase();
    setSearchTerm(searchValue);

    const filtered = users.filter(user => {
      // Concatenate username and userLname to perform search on full name
      const fullName = `${user.username} ${user.userLname}`.toLowerCase();

      return (
        fullName.includes(searchValue) || // Search by full name
        user.email.toLowerCase().includes(searchValue) // Search by email
      );
    });

    setFilteredUsers(filtered);
  };

  const toggleContacted = async (index) => {
    // Update the contacted status of the user
    const updatedUsers = [...users];
    const updatedUser = { ...updatedUsers[index] };

    // Manually toggle contacted status
    updatedUser.contacted = !updatedUser.contacted;

    // Update state immediately for UI feedback
    updatedUsers[index] = updatedUser;
    setUsers(updatedUsers);
    setFilteredUsers(updatedUsers);

    // Send the updated status to the server
    try {
      await axios.post(`${apiBaseUrl}/api/update_contacted_status`, {
        userId: updatedUser._id,
        contacted: updatedUser.contacted,
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('userToken')}`,
        },
      });
    } catch (error) {
      setError('Failed to update contacted status');
    }
  };

  const handlePhoneClick = (phone, index) => {
    // Mark user as contacted
    const updatedUsers = [...users];
    const updatedUser = { ...updatedUsers[index] };
    updatedUser.contacted = true; // Update contacted status to true

    updatedUsers[index] = updatedUser;
    setUsers(updatedUsers);
    setFilteredUsers(updatedUsers);

    // Open WhatsApp in a new tab
    const whatsappMessage = "Hola. Mi nombre es Adriana, martillera de la plataforma Tasacionesya.com. Veo que se registró en la plataforma y me gustaría consultarle si pudo obtener su valuación. De lo contrario, puedo hacer la valuación y enviársela manualmente. Gracias.";

  // Open WhatsApp in a new tab with the default message
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');


    // Optionally, send the updated status to the server
    try {
      axios.post('https://tasacionesya.com:8099/api/update_contacted_status', {
        userId: updatedUser._id,
        contacted: updatedUser.contacted,
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('userToken')}`,
        },
      });
    } catch (error) {
      setError('Failed to update contacted status');
    }
  };

  // Calculate counts
  const totalUsers = users.length;
  const contactedUsersCount = users.filter(user => user.contacted).length;
  const notContactedUsersCount = totalUsers - contactedUsersCount;

  // Inline styles
  const userCountBoxStyle = {
    backgroundColor: '#f8f9fa', // Light gray background
    border: '1px solid #6c757d', // Gray border
    borderRadius: '8px', // Rounded corners
    padding: '20px', // Spacing inside the box
    margin: '10px', // Spacing around the box
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', // Subtle shadow
    transition: 'transform 0.3s', // Smooth scaling
  };

  const userCountTitleStyle = {
    fontWeight: 'bold', // Bold title
    color: '#495057', // Dark gray color for the title
  };

  const userCountValueStyle = {
    fontSize: '1.5rem', // Larger font size for the value
    color: '#2EBF91', // Primary color for the count value
  };

  // Appointment counts
  const totalAppointments = appointments.length;
  const pendingAppointments = appointments.filter(apt => apt.status === 'pending').length;
  const contactedAppointments = appointments.filter(apt => apt.status === 'contacted').length;
  const completedAppointments = appointments.filter(apt => apt.status === 'completed').length;

  return (
    <>
      <CustomNavbar isAuthenticated={true} usertype="Admin" />

      <Container className="mt-5">
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav variant="tabs" className="mb-4">
            <Nav.Item>
              <Nav.Link eventKey="users">
                <FaUser className="me-2" />
                Usuarios Registrados
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="appointments">
                <FaCalendarAlt className="me-2" />
                Citas Martillero
                {pendingAppointments > 0 && (
                  <Badge bg="danger" className="ms-2">{pendingAppointments}</Badge>
                )}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="followups">
                <FaBell className="me-2" />
                Seguimientos
                {followUpsStats.overdue > 0 && (
                  <Badge bg="danger" className="ms-2">{followUpsStats.overdue}</Badge>
                )}
                {followUpsStats.pending > 0 && followUpsStats.overdue === 0 && (
                  <Badge bg="warning" className="ms-2">{followUpsStats.pending}</Badge>
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* Users Tab */}
            <Tab.Pane eventKey="users">
              <h1 className="text-center mb-4">Usuarios registrados</h1>

              {/* User counts display */}
              <Row className="text-center mb-4">
                <Col xs={12} md={4}>
                  <div style={userCountBoxStyle} className="user-count-box">
                    <p style={userCountTitleStyle}>Total de usuarios:</p>
                    <p style={userCountValueStyle}>{totalUsers}</p>
                  </div>
                </Col>
                <Col xs={12} md={4}>
                  <div style={userCountBoxStyle} className="user-count-box">
                    <p style={userCountTitleStyle}>Usuarios contactados:</p>
                    <p style={userCountValueStyle}>{contactedUsersCount}</p>
                  </div>
                </Col>
                <Col xs={12} md={4}>
                  <div style={userCountBoxStyle} className="user-count-box">
                    <p style={userCountTitleStyle}>Usuarios no contactados:</p>
                    <p style={userCountValueStyle}>{notContactedUsersCount}</p>
                  </div>
                </Col>
              </Row>

              <Form className="mb-4">
                <Form.Control
                  type="text"
                  placeholder="Buscar por nombre..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </Form>

              {error && <Alert variant="danger">{error}</Alert>}

              <Row>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user, index) => (
                    <Col key={index} md={4} className="mb-4">
                      <Card className="shadow-sm">
                        <Card.Body>
                          <Card.Title className="text-primary">
                            <FaUser className="me-2" />{user.username} {user.userLname}
                          </Card.Title>
                          <Card.Text>
                            <FaEnvelope className="me-2 text-secondary" />{user.email}
                          </Card.Text>
                          <Card.Text>
                            <FaPhone className="me-2 text-secondary" />
                            <a 
                              href={`#`} 
                              onClick={() => handlePhoneClick(user.phone, index)} 
                              style={{ cursor: 'pointer' }}>
                              {user.phone}
                            </a>
                          </Card.Text>
                          <Card.Text>
                            <FaUserTag className="me-2 text-secondary" />{user.user}
                          </Card.Text>
                          <Card.Text>
                            <FaCalendarAlt className="me-2 text-secondary" />
                            <strong>Créditos Premium:</strong> 
                            <Badge 
                              bg={user.premiumCredits > 0 ? 'success' : 'secondary'} 
                              className="ms-2"
                            >
                              {user.premiumCredits || 0}
                            </Badge>
                          </Card.Text>
                          
                          <Form.Check 
                            type="switch"
                            id={`contacted-switch-${index}`}
                            label={user.contacted ? 'Contactado' : 'Sin contactar'}
                            checked={user.contacted}
                            onChange={() => toggleContacted(index)}
                          />
                        </Card.Body>
                      </Card>
                    </Col>
                  ))
                ) : (
                  !error && <p>No users found</p>
                )}
              </Row>
            </Tab.Pane>

            {/* Appointments Tab */}
            <Tab.Pane eventKey="appointments">
              <h1 className="text-center mb-4">Citas con Martillero</h1>

              {/* Appointment counts display */}
              <Row className="text-center mb-4">
                <Col xs={6} md={3}>
                  <div style={userCountBoxStyle} className="user-count-box">
                    <p style={userCountTitleStyle}>Total:</p>
                    <p style={userCountValueStyle}>{totalAppointments}</p>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div style={userCountBoxStyle} className="user-count-box">
                    <p style={userCountTitleStyle}>Pendientes:</p>
                    <p style={{ ...userCountValueStyle, color: '#ffc107' }}>{pendingAppointments}</p>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div style={userCountBoxStyle} className="user-count-box">
                    <p style={userCountTitleStyle}>Contactados:</p>
                    <p style={{ ...userCountValueStyle, color: '#17a2b8' }}>{contactedAppointments}</p>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div style={userCountBoxStyle} className="user-count-box">
                    <p style={userCountTitleStyle}>Completados:</p>
                    <p style={{ ...userCountValueStyle, color: '#28a745' }}>{completedAppointments}</p>
                  </div>
                </Col>
              </Row>

              {appointmentsError && <Alert variant="danger">{appointmentsError}</Alert>}

              {appointmentsLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3">Cargando citas...</p>
                </div>
              ) : appointments.length > 0 ? (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead className="table-dark">
                      <tr>
                        <th><FaMapMarkerAlt className="me-1" /> Dirección</th>
                        <th><FaPhone className="me-1" /> Teléfono</th>
                        <th><FaEnvelope className="me-1" /> Email</th>
                        <th><FaHome className="me-1" /> Tipo</th>
                        <th>Operación</th>
                        <th>Valor Estimado</th>
                        <th><FaCalendarAlt className="me-1" /> Fecha</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.map((apt) => (
                        <tr key={apt._id}>
                          <td style={{ maxWidth: '200px' }}>{apt.address}</td>
                          <td>
                            <a 
                              href={`https://wa.me/${apt.phone?.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#25D366', fontWeight: 'bold' }}
                            >
                              {apt.phone}
                            </a>
                          </td>
                          <td>{apt.email || '-'}</td>
                          <td>{apt.propertyType || '-'}</td>
                          <td>{apt.operation === 'venta' ? 'Venta' : apt.operation === 'alquiler' ? 'Alquiler' : '-'}</td>
                          <td>{apt.estimatedValue || '-'}</td>
                          <td>{formatDate(apt.createdAt)}</td>
                          <td>
                            <Form.Select 
                              size="sm"
                              value={apt.status}
                              onChange={(e) => updateAppointmentStatus(apt._id, e.target.value)}
                              style={{ minWidth: '120px' }}
                            >
                              <option value="pending">Pendiente</option>
                              <option value="contacted">Contactado</option>
                              <option value="completed">Completado</option>
                              <option value="cancelled">Cancelado</option>
                            </Form.Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert variant="info" className="text-center">
                  <FaCalendarAlt className="me-2" />
                  No hay citas registradas todavía.
                </Alert>
              )}
            </Tab.Pane>

            {/* Follow-ups Tab */}
            <Tab.Pane eventKey="followups">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h1 className="text-center mb-0">Seguimientos de Clientes</h1>
                <button 
                  onClick={openCreateModal}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                  }}
                >
                  <FaPlus /> Nuevo Seguimiento
                </button>
              </div>

              {/* Stats Cards */}
              <Row className="text-center mb-4">
                <Col xs={12} md={3}>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                    color: 'white', 
                    padding: '20px', 
                    borderRadius: '10px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                  }}>
                    <p style={{ fontSize: '16px', margin: 0, opacity: 0.9 }}>Total</p>
                    <p style={{ fontSize: '32px', fontWeight: 'bold', margin: '10px 0 0 0' }}>{followUpsStats.total}</p>
                  </div>
                </Col>
                <Col xs={12} md={3}>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
                    color: 'white', 
                    padding: '20px', 
                    borderRadius: '10px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                  }}>
                    <p style={{ fontSize: '16px', margin: 0, opacity: 0.9 }}>Pendientes</p>
                    <p style={{ fontSize: '32px', fontWeight: 'bold', margin: '10px 0 0 0' }}>{followUpsStats.pending}</p>
                  </div>
                </Col>
                <Col xs={12} md={3}>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 
                    color: 'white', 
                    padding: '20px', 
                    borderRadius: '10px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                  }}>
                    <p style={{ fontSize: '16px', margin: 0, opacity: 0.9 }}>Vencidos</p>
                    <p style={{ fontSize: '32px', fontWeight: 'bold', margin: '10px 0 0 0' }}>{followUpsStats.overdue}</p>
                  </div>
                </Col>
                <Col xs={12} md={3}>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', 
                    color: 'white', 
                    padding: '20px', 
                    borderRadius: '10px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                  }}>
                    <p style={{ fontSize: '16px', margin: 0, opacity: 0.9 }}>Completados</p>
                    <p style={{ fontSize: '32px', fontWeight: 'bold', margin: '10px 0 0 0' }}>{followUpsStats.completed}</p>
                  </div>
                </Col>
              </Row>

              {followUpsError && (
                <Alert variant="danger" className="mt-3">
                  {followUpsError}
                </Alert>
              )}

              {followUpsLoading ? (
                <div className="text-center my-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </Spinner>
                </div>
              ) : followUps.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  {followUps.map((followUp) => {
                    const isOverdue = followUp.reminderDate && new Date(followUp.reminderDate) < new Date() && followUp.status === 'pending';
                    const isDueSoon = followUp.reminderDate && new Date(followUp.reminderDate) < new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) && followUp.status === 'pending' && !isOverdue;
                    
                    return (
                      <Card key={followUp._id} className="mb-3" style={{ 
                        borderLeft: isOverdue ? '5px solid #dc3545' : isDueSoon ? '5px solid #ffc107' : followUp.status === 'completed' ? '5px solid #28a745' : '5px solid #667eea',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                      }}>
                        <Card.Body>
                          <Row>
                            <Col md={8}>
                              <h5 style={{ marginBottom: '15px', color: '#2c3e50' }}>
                                <FaUser className="me-2" style={{ color: '#667eea' }} />
                                {followUp.clientName}
                                {followUp.status === 'completed' && (
                                  <Badge bg="success" className="ms-2">
                                    <FaCheckCircle className="me-1" />
                                    Completado
                                  </Badge>
                                )}
                                {isOverdue && (
                                  <Badge bg="danger" className="ms-2">
                                    <FaClock className="me-1" />
                                    Vencido
                                  </Badge>
                                )}
                                {isDueSoon && (
                                  <Badge bg="warning" className="ms-2">
                                    <FaClock className="me-1" />
                                    Próximo
                                  </Badge>
                                )}
                              </h5>
                              
                              <div style={{ marginBottom: '10px' }}>
                                <FaPhone className="me-2" style={{ color: '#667eea' }} />
                                <a 
                                  href={`https://wa.me/${followUp.clientPhone?.replace(/\D/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#25D366', fontWeight: 'bold', textDecoration: 'none' }}
                                >
                                  {followUp.clientPhone}
                                </a>
                              </div>
                              
                              {followUp.clientEmail && (
                                <div style={{ marginBottom: '10px' }}>
                                  <FaEnvelope className="me-2" style={{ color: '#667eea' }} />
                                  <a href={`mailto:${followUp.clientEmail}`} style={{ color: '#667eea', textDecoration: 'none' }}>
                                    {followUp.clientEmail}
                                  </a>
                                </div>
                              )}
                              
                              {followUp.reminderDate && (
                                <div style={{ marginBottom: '10px' }}>
                                  <FaBell className="me-2" style={{ color: isOverdue ? '#dc3545' : isDueSoon ? '#ffc107' : '#667eea' }} />
                                  <strong>Recordatorio:</strong> {formatDate(followUp.reminderDate)}
                                  {followUp.reminderSent && (
                                    <Badge bg="info" className="ms-2">Email enviado</Badge>
                                  )}
                                </div>
                              )}

                              {/* Notes Section */}
                              {followUp.notes && followUp.notes.length > 0 && (
                                <div style={{ 
                                  background: '#f8f9fa', 
                                  padding: '15px', 
                                  borderRadius: '8px', 
                                  marginTop: '15px',
                                  maxHeight: '200px',
                                  overflowY: 'auto'
                                }}>
                                  <h6 style={{ color: '#667eea', marginBottom: '10px' }}>
                                    <FaStickyNote className="me-2" />
                                    Notas ({followUp.notes.length})
                                  </h6>
                                  {followUp.notes.map((note, index) => (
                                    <div key={index} style={{ 
                                      marginBottom: '10px', 
                                      paddingBottom: '10px', 
                                      borderBottom: index < followUp.notes.length - 1 ? '1px solid #dee2e6' : 'none' 
                                    }}>
                                      <p style={{ margin: '5px 0', fontSize: '13px', color: '#6c757d' }}>
                                        {new Date(note.createdAt).toLocaleString('es-AR', { 
                                          dateStyle: 'short', 
                                          timeStyle: 'short' 
                                        })} - <strong>{note.createdBy}</strong>
                                      </p>
                                      <p style={{ margin: '5px 0', color: '#2c3e50' }}>{note.text}</p>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Add Note Form */}
                              {followUp.status === 'pending' && (
                                <div style={{ marginTop: '15px' }}>
                                  <Form.Group>
                                    <Form.Control
                                      as="textarea"
                                      rows={2}
                                      placeholder="Agregar nota..."
                                      value={newNote}
                                      onChange={(e) => setNewNote(e.target.value)}
                                      style={{ 
                                        borderColor: '#667eea',
                                        resize: 'none'
                                      }}
                                    />
                                    <button
                                      onClick={() => {
                                        handleAddNote(followUp._id);
                                      }}
                                      disabled={!newNote.trim()}
                                      style={{
                                        marginTop: '10px',
                                        background: newNote.trim() ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#ccc',
                                        color: 'white',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        cursor: newNote.trim() ? 'pointer' : 'not-allowed',
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      <FaStickyNote className="me-1" /> Agregar Nota
                                    </button>
                                  </Form.Group>
                                </div>
                              )}
                            </Col>
                            
                            <Col md={4} className="text-end">
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {followUp.status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => handleMarkAsCompleted(followUp._id)}
                                      style={{
                                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      <FaCheckCircle className="me-1" /> Marcar Completado
                                    </button>
                                    <button
                                      onClick={() => openEditModal(followUp)}
                                      style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      <FaEdit className="me-1" /> Editar
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => handleDeleteFollowUp(followUp._id)}
                                  style={{
                                    background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  <FaTrash className="me-1" /> Eliminar
                                </button>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Alert variant="info" className="text-center">
                  <FaBell className="me-2" />
                  No hay seguimientos registrados todavía.
                </Alert>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {/* Follow-Up Modal */}
        {showFollowUpModal && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1050
            }}
            onClick={() => setShowFollowUpModal(false)}
          >
            <div 
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ marginBottom: '25px', color: '#2c3e50' }}>
                {editingFollowUp ? 'Editar Seguimiento' : 'Nuevo Seguimiento'}
              </h3>
              
              <Form onSubmit={editingFollowUp ? handleUpdateFollowUp : handleCreateFollowUp}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 'bold', color: '#667eea' }}>
                    <FaUser className="me-2" />
                    Nombre del Cliente *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    required
                    value={followUpForm.clientName}
                    onChange={(e) => setFollowUpForm({ ...followUpForm, clientName: e.target.value })}
                    placeholder="Ej: Juan Pérez"
                    style={{ borderColor: '#667eea' }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 'bold', color: '#667eea' }}>
                    <FaPhone className="me-2" />
                    Teléfono *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    required
                    value={followUpForm.clientPhone}
                    onChange={(e) => setFollowUpForm({ ...followUpForm, clientPhone: e.target.value })}
                    placeholder="Ej: +54 11 1234-5678"
                    style={{ borderColor: '#667eea' }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 'bold', color: '#667eea' }}>
                    <FaEnvelope className="me-2" />
                    Email
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={followUpForm.clientEmail}
                    onChange={(e) => setFollowUpForm({ ...followUpForm, clientEmail: e.target.value })}
                    placeholder="Ej: juan@email.com"
                    style={{ borderColor: '#667eea' }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 'bold', color: '#667eea' }}>
                    <FaBell className="me-2" />
                    Fecha de Recordatorio
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={followUpForm.reminderDate}
                    onChange={(e) => setFollowUpForm({ ...followUpForm, reminderDate: e.target.value })}
                    style={{ borderColor: '#667eea' }}
                  />
                  <Form.Text className="text-muted">
                    Se enviará un email a info@tasacionesya.com en esta fecha
                  </Form.Text>
                </Form.Group>

                {!editingFollowUp && (
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 'bold', color: '#667eea' }}>
                      <FaStickyNote className="me-2" />
                      Nota Inicial
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={followUpForm.notes}
                      onChange={(e) => setFollowUpForm({ ...followUpForm, notes: e.target.value })}
                      placeholder="Agregar una nota sobre este seguimiento..."
                      style={{ borderColor: '#667eea', resize: 'none' }}
                    />
                  </Form.Group>
                )}

                <div style={{ display: 'flex', gap: '10px', marginTop: '25px' }}>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {editingFollowUp ? 'Actualizar' : 'Crear Seguimiento'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowFollowUpModal(false)}
                    style={{
                      flex: 1,
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </Form>
            </div>
          </div>
        )}
      </Container>
    </>
  );
};

export default Admin;
